import React from "react";
import { Drop, Sep, Msr, useOutside } from "./components";
import { primariesFor, addonsFor, chainFor, setDanceOptions, speedOptions, rowComplete, adultBornYear, genderLabel, effectiveGate, setDanceMeta, needsRoundDance, roundDanceRoundOf, roundDanceOptions, validRoundDance, CUSTOM_BPM_MIN, CUSTOM_BPM_MAX } from "./cascade";
import "./t2.css";

// T2 — Solo entries: the school roster with the inline sideways cascade
// (Competition -> Set dance -> Speed -> Treble reel). Entirely data-driven
// from the event catalog + Layer-1 reference (entry-cascade.md).

function KebabMenu({ paid, onHide }) {
  const [open, setOpen] = React.useState(false);
  const ref = useOutside(open, setOpen);
  return (
    <div className="kebab" ref={ref}>
      <button type="button" className={"kebab-btn" + (open ? " open" : "")} onClick={() => setOpen((o) => !o)} aria-label="More actions">
        <Msr>more_vert</Msr>
      </button>
      {open && (
        <div className="kebab-pop">
          <div className={"kebab-item" + (paid ? " disabled" : "")}
            onClick={() => { if (paid) return; setOpen(false); onHide(); }}>
            <span className="ki-t"><Msr>visibility_off</Msr>Hide</span>
            <span className="ki-d">Remove from this roster. Find them again with "Show hidden".</span>
          </div>
          {paid && <div className="kebab-paidnote"><Msr>lock</Msr>Dancers with paid entries can't be hidden.</div>}
        </div>
      )}
    </div>
  );
}

function SortHead({ label, k, sort, onSort }) {
  const active = sort.key === k;
  const icon = !active ? "unfold_more" : sort.dir === 1 ? "arrow_upward" : "arrow_downward";
  return (
    <button type="button" className={"sort" + (active ? " active" : "")} onClick={() => onSort(k)}>
      {label}<Msr>{icon}</Msr>
    </button>
  );
}

// Toolbar filter select (Age group / Gender). Options come from the roster,
// so the control is data-driven and disappears cleanly when there's nothing
// to filter. Reuses the shared .dd-pop popover from oireachtas.css.
function FilterSelect({ label, icon, resetLabel, value, options, onChange }) {
  const [open, setOpen] = React.useState(false);
  const ref = useOutside(open, setOpen);
  const cur = options.find((o) => o.value === value);
  return (
    <div className="fsel" ref={ref}>
      <button type="button" className={"fsel-btn" + (open ? " open" : "") + (value ? " set" : "")} onClick={() => setOpen((o) => !o)}>
        <Msr>{icon}</Msr>{cur ? cur.label : label}
        <Msr style={{ marginLeft: 2 }}>expand_more</Msr>
      </button>
      {open && (
        <div className="dd-pop">
          <div className={"dd-opt" + (!value ? " sel" : "")} onClick={() => { onChange(""); setOpen(false); }}>
            <span>{resetLabel}</span>{!value && <span className="msr ck-i">check</span>}
          </div>
          {options.map((o) => (
            <div key={o.value} className={"dd-opt" + (value === o.value ? " sel" : "")} onClick={() => { onChange(o.value); setOpen(false); }}>
              <span>{o.label}</span>
              {value === o.value && <span className="msr ck-i">check</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const SPEED_LABEL = (v) => (/^\d/.test(v) ? `${v} bpm` : v);

// A trad-set addon with exactly ONE allowed set (ECRO champ Trad Set U11+ =
// Jockey to the Fair) is FIXED: picking "Add" auto-assigns the set and the
// Set picker is replaced by a static label.
const fixedSetFor = (comp, reference, gate) => {
  if (!comp) return null;
  const opts = setDanceOptions(comp, reference, gate);
  return opts.length === 1 ? opts[0] : null;
};

// Stable identity for dancers with no draft row yet — a fresh object literal
// per render would defeat DancerRow's memo comparison.
const EMPTY_ROW = { dancer_id: null, primaryId: null, setKey: "", speed: "", custom: "", treble: null, trebleId: null };

// One roster row. Memoized so typing in the search box / toggling filters /
// editing ONE dancer's cascade doesn't re-render the other 99 rows of a big
// school (the parent patches `rows` immutably per dancer, so untouched rows
// keep their object identity). The comparator checks data props only —
// callback props are closures re-created by the parent each render but their
// behavior is stable, so they are deliberately ignored.
const DancerRow = React.memo(function DancerRow({ d, row, competitions, reference, setsByKey, adultTo, incomplete, onPatchRow, onRequestHide, onRestore }) {
  const id = String(d._id);
  const gender = genderLabel(d, adultTo);

  if (d.hidden) {
    return (
      <div className="rt-row muted">
        <div className="dn">{d.fname} {d.lname}</div>
        <div className="cell-m cell-age">{(d.birthday || "").slice(0, 4)}</div>
        <div className="cell-m cell-gender">{gender}</div>
        <div className="reg">
          <span className="dbadge hid"><Msr>visibility_off</Msr>Hidden</span>
          <button type="button" className="restorebtn" onClick={() => onRestore(d)}><Msr>restart_alt</Msr>Restore</button>
        </div>
        <div className="rt-kebab"></div>
      </div>
    );
  }

  const patch = (p) => onPatchRow(id, p);
  const paidCompIds = ((d.entry && d.entry.competitions) || []).map(String);
  const paidPrimary = paidCompIds
    .map((cid) => competitions.find((c) => String(c._id) === cid))
    .find((c) => c && c.role === "primary");

  // Add-later: the dancer's primary is PAID — it becomes fixed context
  // (one primary per dancer) and only its still-unpaid linked addons
  // are offered. The backend accepts an addon against a paid primary.
  if (paidPrimary) {
    const unpaidAddons = addonsFor(competitions, d, paidPrimary)
      .filter((a) => !paidCompIds.includes(String(a._id)));
    const unpaidTrebles = unpaidAddons.filter((a) => a.type === "treble_reel");
    const unpaidTradSets = unpaidAddons.filter((a) => a.type === "trad_set");
    const trebleOpts = unpaidTrebles
      .map((a) => ({ value: String(a._id), label: `Add (${a.code})` }))
      .concat([{ value: "__none", label: "Treble reel: Not entering" }]);
    const tradSetOpts = unpaidTradSets
      .map((a) => ({ value: String(a._id), label: `Add (${a.code})` }))
      .concat([{ value: "__none", label: "Trad set: Not entering" }]);
    const tsCompPaid = row.tradSetId ? competitions.find((c) => String(c._id) === row.tradSetId) : null;
    const tsKeyOpts = tsCompPaid ? setDanceOptions(tsCompPaid, reference, d.setDanceGate).map((s) => ({
      value: s.key, label: s.name, meta: "traditional",
    })) : [];
    // Trust the UNPAID lists, not the raw row flags. A draft rehydrated from a
    // cart that still holds already-PAID add-ons re-marks them "add"
    // (cartItemsToRows sees an addon competition and can't tell it was paid),
    // which lit the green "adding" marker for an add-on already bought —
    // sometimes with no visible control at all, because the dropdowns DO filter
    // by unpaid. The backend now prunes the cart at checkout; this keeps carts
    // that are already stale honest, and keeps one source of truth in the row.
    const pendingAddon = (list, id) => !!id && list.some((a) => String(a._id) === String(id));
    const addingTreble = row.treble === "add" && pendingAddon(unpaidTrebles, row.trebleId);
    const addingTradSet = row.tradSet === "add" && pendingAddon(unpaidTradSets, row.tradSetId);
    // A half-configured trad set (Add picked, set choice pending) blocks the
    // done state — cascade.js serializes nothing for the row until it's whole.
    const adding =
      (addingTreble || (addingTradSet && !!row.tradSetKey)) &&
      !(addingTradSet && !row.tradSetKey);
    return (
      <div className={"rt-row" + (adding ? " done" : "")}>
        <div className="dn">{d.fname} {d.lname}</div>
        <div className="cell-m cell-age">{(d.birthday || "").slice(0, 4)}</div>
        <div className="cell-m cell-gender">{gender}</div>
        <div className="reg">
          <span className="ent"><Msr>check_circle</Msr>{paidPrimary.name || paidPrimary.code}</span>
          {unpaidTrebles.length > 0 && (
            <>
              <Sep />
              <Drop value={row.treble === "add" ? row.trebleId : "__none"}
                placeholder="Treble reel" options={trebleOpts}
                onChange={(v) => patch(v === "__none" ? { treble: "none", trebleId: null } : { treble: "add", trebleId: v })}
                onClear={row.treble === "add" ? () => patch({ treble: "none", trebleId: null }) : undefined} clearLabel="Remove treble reel" />
            </>
          )}
          {unpaidTradSets.length > 0 && (
            <>
              <Sep />
              <Drop value={row.tradSet === "add" ? row.tradSetId : "__none"}
                placeholder="Trad set" options={tradSetOpts}
                onChange={(v) => {
                  if (v === "__none") return patch({ tradSet: "none", tradSetId: null, tradSetKey: "" });
                  const fixed = fixedSetFor(competitions.find((c) => String(c._id) === v), reference, d.setDanceGate);
                  patch({ tradSet: "add", tradSetId: v, tradSetKey: fixed ? fixed.key : "" });
                }}
                onClear={row.tradSet === "add" ? () => patch({ tradSet: "none", tradSetId: null, tradSetKey: "" }) : undefined} clearLabel="Remove trad set" />
            </>
          )}
          {addingTradSet && tsKeyOpts.length === 1 && (
            <>
              <Sep />
              <span className="fixedset">{tsKeyOpts[0].label}</span>
            </>
          )}
          {addingTradSet && tsKeyOpts.length !== 1 && (
            <>
              <Sep />
              <Drop value={row.tradSetKey} placeholder="Choose a set to finish" reqEmpty options={tsKeyOpts}
                onChange={(v) => patch({ tradSetKey: v })}
                onClear={() => patch({ tradSetKey: "" })} clearLabel="Clear set" />
            </>
          )}
          {incomplete && <div className="solo-incomplete-note">Choose the missing add-on details to save this dancer.</div>}
        </div>
        <div className="rt-kebab">
          <KebabMenu paid onHide={() => {}} />
        </div>
      </div>
    );
  }

  const primaries = primariesFor(competitions, d);
  const comp = row.primaryId ? competitions.find((c) => String(c._id) === row.primaryId) : null;
  const chain = comp ? chainFor(comp) : [];
  const setDance = row.setKey ? setsByKey[row.setKey] : null;
  const addons = comp ? addonsFor(competitions, d, comp) : [];

  const trebleAddons = addons.filter((a) => a.type === "treble_reel");
  const tradSetAddons = addons.filter((a) => a.type === "trad_set");

  // One gate drives the set list, the label and whether a speed is asked for —
  // never derive them separately (see cascade.effectiveGate).
  const gate = comp ? effectiveGate(comp, d.setDanceGate) : null;
  const fixedTempo = gate === "traditional";
  // Round-dance choice (Adult champs: round 2 Slip Jig or Reel; U7: round 3
  // Light Jig or Slow Treble Jig) sits right after the competition and gates
  // the rest. U7 has no set dance, so nothing follows it there.
  const showRound = comp && needsRoundDance(comp);
  const roundNo = showRound ? roundDanceRoundOf(comp) : null;
  const roundDone = !showRound || validRoundDance(comp, row.roundDance);
  const showSet = comp && chain.includes("setDance") && roundDone;
  const needsSpeed = chain.includes("speed") && setDance && !fixedTempo;
  const showSpeed = showSet && !!row.setKey && chain.includes("speed") && !fixedTempo;
  const speedDone = !chain.includes("speed") || fixedTempo ||
    (row.speed && (row.speed !== "__custom" ||
      (/^\d{2,3}$/.test(row.custom) && +row.custom >= CUSTOM_BPM_MIN && +row.custom <= CUSTOM_BPM_MAX)));
  const showTreble = comp && roundDone && (!showSet || !!row.setKey) && speedDone && trebleAddons.length > 0;
  const showTradSet = comp && roundDone && (!showSet || !!row.setKey) && speedDone && tradSetAddons.length > 0;
  const complete = comp && rowComplete(row, comp, setDance, gate);
  const paidComps = paidCompIds;

  const compOpts = primaries
    .filter((c) => !paidComps.includes(String(c._id)))
    .map((c) => ({ value: String(c._id), label: c.name || c.code, meta: (c.type || "").replace(/_/g, " ") }));
  const setOpts = comp ? setDanceOptions(comp, reference, d.setDanceGate).map((s) => ({
    value: s.key, label: s.name, meta: setDanceMeta(s, gate),
  })) : [];
  const roundOpts = showRound ? roundDanceOptions(comp, reference).map((r) => ({ value: r.key, label: r.name, meta: `round ${roundNo}` })) : [];
  const speeds = needsSpeed ? speedOptions(setDance, reference, gate) || [] : [];
  const speedOpts = speeds.map((s) => ({ value: s, label: SPEED_LABEL(s) })).concat([{ value: "__custom", label: "Custom…" }]);
  const trebleOpts = trebleAddons.map((a) => ({ value: String(a._id), label: `Add (${a.code})` })).concat([{ value: "__none", label: "Treble reel: Not entering" }]);
  const tradSetOpts = tradSetAddons.map((a) => ({ value: String(a._id), label: `Add (${a.code})` })).concat([{ value: "__none", label: "Trad set: Not entering" }]);
  const tsComp = row.tradSetId ? competitions.find((c) => String(c._id) === row.tradSetId) : null;
  const tsKeyOpts = tsComp ? setDanceOptions(tsComp, reference, d.setDanceGate).map((s) => ({
    value: s.key, label: s.name, meta: "traditional",
  })) : [];

  return (
    <div className={"rt-row" + (complete ? " done" : "")}>
      <div className="dn">{d.fname} {d.lname}</div>
      <div className="cell-m cell-age">{(d.birthday || "").slice(0, 4)}</div>
      <div className="cell-m cell-gender">{gender}</div>

      <div className="reg">
        {paidComps.length > 0 && <span className="ent"><Msr>check_circle</Msr>Registered</span>}
        <div className="cascade">
          <Drop value={row.primaryId || ""} placeholder="Competition…" options={compOpts}
            onChange={(v) => patch({ primaryId: v, roundDance: "", setKey: "", speed: "", custom: "", treble: null, trebleId: null, tradSet: null, tradSetId: null, tradSetKey: "" })}
            onClear={() => patch({ primaryId: null, roundDance: "", setKey: "", speed: "", custom: "", treble: null, trebleId: null, tradSet: null, tradSetId: null, tradSetKey: "" })} />

          {showRound && <Sep />}
          {showRound && (
            <Drop value={validRoundDance(comp, row.roundDance) ? row.roundDance : ""} placeholder={`Round ${roundNo} dance`} reqEmpty options={roundOpts}
              onChange={(v) => patch({ roundDance: v })}
              onClear={() => patch({ roundDance: "", setKey: "", speed: "", custom: "", treble: null, tradSet: null, tradSetId: null, tradSetKey: "" })}
              clearLabel={`Clear round ${roundNo} dance`} />
          )}

          {showSet && <Sep />}
          {showSet && (
            <Drop value={row.setKey} placeholder="Set dance" reqEmpty options={setOpts}
              onChange={(v) => patch({ setKey: v, speed: "", custom: "" })}
              onClear={() => patch({ setKey: "", speed: "", custom: "", treble: null, tradSet: null, tradSetId: null, tradSetKey: "" })}
              clearLabel="Clear set dance" />
          )}

          {showSpeed && <Sep />}
          {/* The dropdown stays rendered alongside the custom input — replacing
              it left no way back from Custom… without clearing the whole set
              (staging user testing, 2026-08-20). */}
          {showSpeed && (
            <Drop value={row.speed} placeholder="Speed" reqEmpty options={speedOpts}
              onChange={(v) => patch(v === "__custom" ? { speed: v } : { speed: v, custom: "" })}
              onClear={() => patch({ speed: "", custom: "", treble: null, tradSet: null, tradSetId: null, tradSetKey: "" })} clearLabel="Clear speed" />
          )}
          {showSpeed && row.speed === "__custom" && (
            <input className="custom-bpm" type="text" inputMode="numeric" placeholder="50–130"
              value={row.custom} onChange={(e) => patch({ custom: e.target.value.replace(/[^0-9]/g, "").slice(0, 3) })} />
          )}

          {showTreble && <Sep />}
          {showTreble && (
            <Drop value={row.treble === "add" ? row.trebleId : "__none"}
              placeholder="Treble reel" options={trebleOpts}
              onChange={(v) => patch(v === "__none" ? { treble: "none", trebleId: null } : { treble: "add", trebleId: v })}
              onClear={row.treble === "add" ? () => patch({ treble: "none", trebleId: null }) : undefined} clearLabel="Remove treble reel" />
          )}

          {showTradSet && <Sep />}
          {showTradSet && (
            <Drop value={row.tradSet === "add" ? row.tradSetId : "__none"}
              placeholder="Trad set" options={tradSetOpts}
              onChange={(v) => {
                if (v === "__none") return patch({ tradSet: "none", tradSetId: null, tradSetKey: "" });
                const fixed = fixedSetFor(competitions.find((c) => String(c._id) === v), reference, d.setDanceGate);
                patch({ tradSet: "add", tradSetId: v, tradSetKey: fixed ? fixed.key : "" });
              }}
              onClear={row.tradSet === "add" ? () => patch({ tradSet: "none", tradSetId: null, tradSetKey: "" }) : undefined} clearLabel="Remove trad set" />
          )}
          {showTradSet && row.tradSet === "add" && <Sep />}
          {showTradSet && row.tradSet === "add" && (tsKeyOpts.length === 1
            ? <span className="fixedset">{tsKeyOpts[0].label}</span>
            : <Drop value={row.tradSetKey} placeholder="Choose a set to finish" reqEmpty options={tsKeyOpts}
                onChange={(v) => patch({ tradSetKey: v })}
                onClear={() => patch({ tradSetKey: "" })} clearLabel="Clear set" />)}
        </div>
        {incomplete && <div className="solo-incomplete-note">Complete the highlighted fields to save this dancer.</div>}
      </div>

      <div className="rt-kebab">
        <KebabMenu paid={paidComps.length > 0} onHide={() => onRequestHide(d)} />
      </div>
    </div>
  );
}, (prev, next) =>
  prev.d === next.d &&
  prev.row === next.row &&
  prev.competitions === next.competitions &&
  prev.reference === next.reference &&
  prev.setsByKey === next.setsByKey &&
  prev.adultTo === next.adultTo &&
  prev.incomplete === next.incomplete
);

export default function SoloEntries({ roster, competitions, reference, rows, onPatchRow, onHide, onRestore, showHidden, setShowHidden, incompleteIds = [], showIncomplete = false, setShowIncomplete }) {
  const [q, setQ] = React.useState("");
  const [notEntered, setNotEntered] = React.useState(false);
  const [fAge, setFAge] = React.useState("");
  const [fGender, setFGender] = React.useState("");
  const [confirm, setConfirm] = React.useState(null);
  const [reviewIds, setReviewIds] = React.useState([]);
  // Keep dancers reached through Review visible after they become complete,
  // so their newly revealed optional add-ons remain available until review ends.
  React.useEffect(() => {
    setReviewIds((ids) => {
      if (!showIncomplete) return ids.length ? [] : ids;
      const added = incompleteIds.filter((id) => !ids.includes(id));
      return added.length ? [...ids, ...added] : ids;
    });
  }, [showIncomplete, incompleteIds]);
  React.useEffect(() => {
    if (showIncomplete) {
      setQ("");
      setFAge("");
      setFGender("");
      setNotEntered(false);
    }
  }, [showIncomplete]);
  const [sort, setSort] = React.useState({ key: "lname", dir: 1 });
  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: 1 }));

  // Filter options are derived from the roster (the Age column shows the
  // birth year, so the "Age group" filter keys on that too).
  const ageOptions = React.useMemo(() => {
    const years = [...new Set(roster.map((d) => (d.birthday || "").slice(0, 4)).filter(Boolean))];
    return years.sort((a, b) => Number(b) - Number(a)).map((y) => ({ value: y, label: y }));
  }, [roster]);
  // Adult cut-off comes from the catalog (see cascade.adultBornYear) and drives
  // the split gender labels — the filter keys on the SAME labels the rows show.
  const adultTo = React.useMemo(() => adultBornYear(competitions), [competitions]);
  const genderOptions = React.useMemo(() => {
    const gs = [...new Set(roster.map((d) => genderLabel(d, adultTo)).filter(Boolean))];
    return gs.sort().map((g) => ({ value: g, label: g }));
  }, [roster, adultTo]);

  const setsByKey = React.useMemo(
    () => Object.fromEntries((reference.setDances || []).map((s) => [s.key, s])),
    [reference]
  );

  const visible = roster.filter((d) => {
    if (d.hidden && !showHidden) return false;
    if (showIncomplete && !reviewIds.includes(String(d._id)) && !incompleteIds.includes(String(d._id))) return false;
    const name = `${d.fname} ${d.lname}`.toLowerCase();
    if (q && !name.includes(q.toLowerCase())) return false;
    if (fAge && (d.birthday || "").slice(0, 4) !== fAge) return false;
    if (fGender && genderLabel(d, adultTo) !== fGender) return false;
    const paid = d.entry && (d.entry.competitions || []).length > 0;
    // Keep drafts visible while the teacher finishes or adds optional extras.
    if (notEntered && !d.hidden && paid && !incompleteIds.includes(String(d._id))) return false;
    return true;
  });
  // Dancer sorts on SURNAME (first name only breaks ties) — a teacher scanning
  // a 70-dancer roster looks for the family name. Gender sorts on the displayed
  // label, not the stored compound, or Girls and Ladies collapse into one block.
  const sortVal = (d) => {
    if (sort.key === "gender") return genderLabel(d, adultTo);
    if (sort.key === "lname") return `${d.lname || ""} ${d.fname || ""}`;
    return d[sort.key];
  };
  visible.sort((a, b) => {
    const av = String(sortVal(a) || "").toLowerCase();
    const bv = String(sortVal(b) || "").toLowerCase();
    return av < bv ? -sort.dir : av > bv ? sort.dir : 0;
  });

  const requestHide = (d) => {
    const row = rows[String(d._id)];
    if (row && row.primaryId) setConfirm(d);
    else onHide(d);
  };

  return (
    <div>
      <div className="toolbar">
        <div className="search">
          <Msr>search</Msr>
          <input placeholder="Search dancers by name" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        {ageOptions.length > 1 && (
          <FilterSelect label="Age group" icon="cake" resetLabel="All ages" value={fAge} options={ageOptions} onChange={setFAge} />
        )}
        {genderOptions.length > 1 && (
          <FilterSelect label="Gender" icon="wc" resetLabel="All genders" value={fGender} options={genderOptions} onChange={setFGender} />
        )}
        <div className={"toggle-f" + (notEntered ? " on" : "")} onClick={() => setNotEntered((v) => !v)}>
          <span className="sw"></span>Not entered yet
        </div>
        {(incompleteIds.length > 0 || showIncomplete) && (
          <button type="button" className={"toggle-f" + (showIncomplete ? " on" : "")}
            aria-pressed={showIncomplete} onClick={() => setShowIncomplete(!showIncomplete)}>
            Show incomplete ({incompleteIds.length})
          </button>
        )}
        <div className={"toggle-f" + (showHidden ? " on" : "")} onClick={() => setShowHidden(!showHidden)}>
          <span className="sw"></span>Show hidden
        </div>
      </div>

      <div className="card">
        <div className="rt-head">
          <span><SortHead label="Dancer" k="lname" sort={sort} onSort={toggleSort} /></span>
          <span><SortHead label="Age" k="birthday" sort={sort} onSort={toggleSort} /></span>
          <span><SortHead label="Gender" k="gender" sort={sort} onSort={toggleSort} /></span>
          <span>Registration</span><span></span>
        </div>

        {showIncomplete && visible.length === 0 && <p className="helper">No incomplete entries match these filters.</p>}
        {visible.map((d) => (
          <DancerRow key={String(d._id)} d={d} row={rows[String(d._id)] || EMPTY_ROW}
            competitions={competitions} reference={reference} setsByKey={setsByKey} adultTo={adultTo} incomplete={incompleteIds.includes(String(d._id))}
            onPatchRow={onPatchRow} onRequestHide={requestHide} onRestore={onRestore} />
        ))}
      </div>

      {showIncomplete && <div className="helper">Completed dancers stay visible so you can add extras. Turn off "Show incomplete" to return to the full roster.</div>}

      <div className="helper">
        <Msr>info</Msr>
        Don't see a dancer? Their parent must add them to your school first.
      </div>

      {confirm && (
        <div className="modal-scrim" onClick={() => setConfirm(null)}>
          <div className="omodal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-ic"><Msr>visibility_off</Msr></div>
            <h3>Hide {confirm.fname} {confirm.lname}?</h3>
            <p>This clears their draft entries. You can restore {confirm.fname} at any time with "Show hidden".</p>
            <div className="modal-act">
              <button type="button" className="mbtn ghost" onClick={() => setConfirm(null)}>Cancel</button>
              <button type="button" className="mbtn danger" onClick={() => { onHide(confirm); setConfirm(null); }}>Hide</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
